<template>
    <div class="checkout-layout">
        <LayoutSkipLinks />
        <div class="grid grid-cols-1 lg:grid-cols-12 lg:min-h-screen">
            <div class="sidebar order-1 lg:flex lg:flex-col lg:items-start lg:gap-4 lg:col-span-5 lg:order-2 bg-base-200">
                <header class="flex justify-between items-center gap-4 w-full px-4 py-5 lg:px-6 lg:py-5 lg:max-w-[496px]">
                    <LayoutLogo />

                    <h1 class="sr-only">{{ $t('checkout.metaTitle') }}</h1>

                    <client-only>
                        <div v-if="isLoggedIn || isGuestSession" class="flex flex-col justify-between text-xs">
                            <div>{{ user?.email }}</div>
                            <div>
                                {{ $t('checkout.summary.logout') }}
                                <MiscLink
                                    to=""
                                    class="link underline text-primary font-semibold h-6 inline-block"
                                    tabindex="0"
                                    @click="logout()"
                                    @keyup.enter="logout()"
                                >
                                    {{ $t('checkout.summary.logoutLink') }}
                                </MiscLink>
                            </div>
                        </div>
                    </client-only>
                </header>
                <div class="collapse bg-base-100 lg:collapse-open lg:max-w-[496px] lg:px-6 lg:bg-base-200">
                    <input
                        id="toggleCart"
                        v-model="showCart"
                        class="peer lg:hidden"
                        type="checkbox"
                    >
                    <label for="toggleCart" class="collapse-title pr-4 font-medium !bg-base-100 lg:hidden">
                        <span class="text-sm leading-4 align-middle" v-text="$t('checkout.summary.toggleLabel')" />
                        <MiscIcon name="chevron-down" class="inline-block ml-1 leading-4 align-middle transition-transform origin-center lg:hidden" :class="{ '-scale-100': showCart }" />
                        <span v-if="cart != null" class="inline-block leading-7 float-right text-xl text-primary font-bold lg:hidden">{{ getFormattedPrice(cart.price?.totalPrice) }}</span>
                    </label>
                    <div class="collapse-content p-0">
                        <Cart :is-interactive="false" />
                        <label for="toggleCart" class="w-full flex justify-center items-center p-1 border-t border-b bg-base-200 lg:hidden">
                            <span class="btn btn-tertiary">
                                {{ $t('checkout.summary.hideLabel') }}
                                <MiscIcon name="chevron-up" class="lg:hidden ml-2" />
                            </span>
                        </label>
                        <div class="hidden mt-6 lg:block bg-base-100">
                            <CheckoutServiceBox />
                        </div>
                    </div>
                </div>
            </div>
            <main class="content lg:col-span-7 order-2 lg:order-1 container max-w-none mx-auto p-4 lg:px-6 lg:py-9 lg:border-r">
                <div class="w-full lg:max-w-[720px] lg:ml-auto">
                    <slot />
                </div>
            </main>
        </div>
        <div class="w-full grid grid-cols-1 bg-base-200 border-t border-b border-secondary lg:sticky lg:left-0 lg:bottom-0 lg:grid-cols-12 lg:border-t-0 lg:border-b-0">
            <div class="px-4 pt-5 pb-3 bg-base-100 lg:col-span-7 lg:py-4 lg:px-6 lg:border-t lg:border-r">
                <div class="lg:max-w-[720px] lg:ml-auto">
                    <div id="checkoutNavigation" />
                    <CheckoutServiceBox class="lg:hidden" />
                </div>
            </div>
        </div>
        <div class="w-full grid grid-cols-1 lg:grid-cols-12 lg:bg-base-200">
            <LayoutFooterLight :service-navigation="serviceNavigation" class="lg:col-span-7 lg:border-t lg:border-r" />
        </div>
        <LayoutCountryProposal />
        <LayoutNotifications />
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { layoutCmsIncludes } from '~/utils/includes/cms'
import { toIncludesQueryParams } from '~/utils/includesQueryParams'
import { stripCmsPage } from '~/utils/cmsPayloadStrip'

const nuxtApp = useNuxtApp()
const { apiClient } = useShopwareContext()
const { cart } = useCart()
const { getFormattedPrice } = usePrice()
const showCart = ref(false)
const { user, logout, isLoggedIn, isGuestSession } = useUser()

const { data: layoutCms, error: layoutCmsError } = await useAsyncData(async () => {
    const { data } = await apiClient.invoke('getLayoutCMS get /layout-cms', {
        query: toIncludesQueryParams(layoutCmsIncludes),
    })
    stripCmsPage(data.header)
    stripCmsPage(data.footer)
    return data
})

const serviceNavigation = computed(() => {
    const footerBlock = layoutCms.value?.footer?.sections[0]?.blocks?.find((block: Schemas['CmsBlock']) => block.type === 'a4g_footer')
    return (footerBlock?.slots[0] as Schemas['CmsElementA4gFooter'] | undefined)?.config?.bottomNavigation?.value ?? []
})

if (layoutCmsError.value) {
    showError(layoutCmsError.value as Error)
}

useRuntimeHook('page:finish', () => {
    window.scrollTo(0, 0)
})

const hasTrackedCheckout = ref(false)

watch(cart, (currentCart) => {
    // Fire the event once the cart is loaded (either with items or empty)
    if (!hasTrackedCheckout.value && currentCart !== undefined) {
        hasTrackedCheckout.value = true
        nuxtApp.callHook('viewCheckout', { cart: currentCart })
    }
}, { immediate: true })
</script>
